import { FC, Fragment, useEffect, useMemo, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useNavigateTo from 'hooks/useNavigateTo';
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Models
import IQuiz from 'models/Quiz';
// Acctions
import { resetAnswersScore } from 'store/player/playerSlice';
// Selectors
import { selectNextQuestion, selectQuestion, selectQuestionIndex, selectQuestionsLength, selectQuiz } from 'store/quiz/quizSelectors';
import { selectPageIndex, selectPages } from 'store/pages/pagesSelectors';
import { selectAnswers, selectTotalScore } from 'store/player/playerSelectors';
// Components
import AppBar from 'components/AppBar';
import PoweredBy from 'components/PoweredBy';
import { Button } from 'components/Buttons';

const StreakHandlerPage:FC = () => {
  const { t } = useTranslation();

  const navigateTo = useNavigateTo();
  const dispatch = useAppDispatch();

  const [ready, setReady] = useState(false);

  const questionIndex = useAppSelector(selectQuestionIndex);
  const pages = useAppSelector(selectPages);
  const pageIndex = useAppSelector(selectPageIndex);
  const answers = useAppSelector(selectAnswers);
  const totalScore = useAppSelector(selectTotalScore);
  
  const quiz:IQuiz | null = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const nextQuestion = useAppSelector(selectNextQuestion);
  const questionsLength = useAppSelector(selectQuestionsLength);

  const lastAnswer = useMemo(() => answers[answers.length - 1], [answers]);

  useEffect(() => {
    if (!lastAnswer?.correct) {
      dispatch(resetAnswersScore());
      setTimeout(() => {
        navigateTo('scoring-result');
      }, 5000);
    }
    setReady(true);
    // eslint-disable-next-line
  }, [lastAnswer?.correct, questionIndex, questionsLength]);

  const bankMyPoints = () => {
    navigateTo('scoring-result');
  }

  const handleContinue = () => {
    const hasMorePages = pageIndex + 1 < pages.length;
    const nextPath = hasMorePages ? 'pages-handler' : 'scoring-result';
    navigateTo(nextPath);
  }

  const lastAnswerIncorrect = lastAnswer && !lastAnswer.correct;

  if (!quiz || !ready) return null;
  return (
    <div
      className="p-4 flex flex-col w-full h-full bg-gray-200 overflow-y-auto"
      style={{ background: quiz.backgroundImage ? `url('${quiz.backgroundImage}') center / cover` : '' }}
    >
      <AppBar />
      <div className="flex-1 text-primary pt-8">
        <p className="text-center text-md uppercase mb-4">{`${t('labels.question')} ${questionIndex} / ${questionsLength}`}</p>
        <h1 className="text-center text-5xl font-bold uppercase text-[#ff9100] mb-8">
          {lastAnswerIncorrect ? t('labels.incorrect') : t('labels.correct')}
        </h1>

        {question.postAnswerImage ? (
          <img src={question.postAnswerImage} alt="" className="mb-8" />
        ) : null}

        {lastAnswerIncorrect ? (
          <p className="text-center text-md uppercase mb-4">{t('pages.streakHandlerPage.incorrectText')}</p>
        ) : (
          <Fragment>
            {nextQuestion ? (
              <p className="text-center text-md uppercase mb-4">{t('pages.streakHandlerPage.bankOrContinue')}</p>
            ) : null}
            <p className="text-center text-md uppercase">{t('labels.currentTotal')}</p>
            <p className="text-center text-2xl font-bold uppercase mb-4">{`${totalScore} ${t('labels.points')}`}</p>
            {nextQuestion ? (
              <Fragment>
                <p className="text-center text-md uppercase  mb-4">{t('pages.streakHandlerPage.youCouldWin', { points: nextQuestion?.score })}</p>
                 <Button
                    className="w-full flex items-center justify-center gap-3 uppercase mb-4 text-black"
                    onClick={bankMyPoints}
                  >
                    {t('pages.streakHandlerPage.bankMyPointsButton')}
                  </Button>
              </Fragment>
            ) : null}
            <Button
              className="w-full flex items-center justify-center gap-3 uppercase primary-button"
              onClick={handleContinue}
            >
              {nextQuestion ? t('pages.streakHandlerPage.continuePlayingButton') : t('buttons.continue')}
            </Button>
          </Fragment>
        )}
      </div>
      <PoweredBy />
    </div>
  )
}

export default StreakHandlerPage;
